/**
 * Language Code Mapping Constants
 * Maps between ISO 639-1 (2-letter) and ISO 639-3 (3-letter) language codes
 * Based on languages supported by the backend API
 */

/**
 * Mapping from 2-letter (ISO 639-1) to 3-letter (ISO 639-3) language codes
 * Only includes languages that are supported by the backend
 */
export const LANGUAGE_CODE_2_TO_3: { [key: string]: string } = {
  'af': 'afr', // Afrikaans
  'am': 'amh', // Amharic
  'ar': 'ara', // Arabic
  'ay': 'aym', // Aymara
  'az': 'aze', // Azerbaijani
  'be': 'bel', // Belarusian
  'bn': 'ben', // Bengali
  'bi': 'bis', // Bislama
  'bs': 'bos', // Bosnian
  'bg': 'bul', // Bulgarian
  'ca': 'cat', // Catalan
  'cs': 'ces', // Czech
  'ch': 'cha', // Chamorro
  'da': 'dan', // Danish
  'de': 'deu', // German
  'dv': 'div', // Maldivian
  'dz': 'dzo', // Dzongkha
  'el': 'ell', // Greek
  'en': 'eng', // English
  'et': 'est', // Estonian
  'fo': 'fao', // Faroese
  'fa': 'fas', // Persian
  'fj': 'fij', // Fijian
  'fi': 'fin', // Finnish
  'fr': 'fra', // French
  'ga': 'gle', // Irish
  'gv': 'glv', // Manx
  'gn': 'grn', // Guaraní
  'ht': 'hat', // Haitian Creole
  'he': 'heb', // Hebrew
  'hz': 'her', // Herero
  'hi': 'hin', // Hindi
  'ho': 'hmo', // Hiri Motu
  'hr': 'hrv', // Croatian
  'hu': 'hun', // Hungarian
  'hy': 'hye', // Armenian
  'id': 'ind', // Indonesian
  'is': 'isl', // Icelandic
  'it': 'ita', // Italian
  'ja': 'jpn', // Japanese
  'kl': 'kal', // Greenlandic
  'ka': 'kat', // Georgian
  'kk': 'kaz', // Kazakh
  'km': 'khm', // Khmer
  'rw': 'kin', // Kinyarwanda
  'ky': 'kir', // Kyrgyz
  'kg': 'kon', // Kikongo
  'ko': 'kor', // Korean
  'lo': 'lao', // Lao
  'la': 'lat', // Latin
  'lv': 'lav', // Latvian
  'ln': 'lin', // Lingala
  'lt': 'lit', // Lithuanian
  'lb': 'ltz', // Luxembourgish
  'mh': 'mah', // Marshallese
  'mk': 'mkd', // Macedonian
  'mg': 'mlg', // Malagasy
  'mt': 'mlt', // Maltese
  'mn': 'mon', // Mongolian
  'mi': 'mri', // Māori
  'ms': 'msa', // Malay
  'my': 'mya', // Burmese
  'na': 'nau', // Nauru
  'nr': 'nbl', // Southern Ndebele
  'nd': 'nde', // Northern Ndebele
  'ng': 'ndo', // Ndonga
  'ne': 'nep', // Nepali
  'nl': 'nld', // Dutch
  'nn': 'nno', // Norwegian Nynorsk
  'nb': 'nob', // Norwegian Bokmål
  'no': 'nor', // Norwegian
  'ny': 'nya', // Chewa
  'pl': 'pol', // Polish
  'pt': 'por', // Portuguese
  'qu': 'que', // Quechua
  'rm': 'roh', // Romansh
  'ro': 'ron', // Romanian
  'rn': 'run', // Kirundi
  'ru': 'rus', // Russian
  'sg': 'sag', // Sango
  'si': 'sin', // Sinhala
  'sk': 'slk', // Slovak
  'sl': 'slv', // Slovene
  'sm': 'smo', // Samoan
  'sn': 'sna', // Shona
  'so': 'som', // Somali
  'st': 'sot', // Sotho
  'es': 'spa', // Spanish
  'sq': 'sqi', // Albanian
  'sr': 'srp', // Serbian
  'ss': 'ssw', // Swazi
  'sw': 'swa', // Swahili
  'sv': 'swe', // Swedish
  'ta': 'tam', // Tamil
  'tg': 'tgk', // Tajik
  'th': 'tha', // Thai
  'ti': 'tir', // Tigrinya
  'to': 'ton', // Tongan
  'tn': 'tsn', // Tswana
  'ts': 'tso', // Tsonga
  'tk': 'tuk', // Turkmen
  'tr': 'tur', // Turkish
  'uk': 'ukr', // Ukrainian
  'ur': 'urd', // Urdu
  'uz': 'uzb', // Uzbek
  've': 'ven', // Venda
  'vi': 'vie', // Vietnamese
  'xh': 'xho', // Xhosa
  'zh': 'zho', // Chinese
  'zu': 'zul', // Zulu
};

/**
 * Additional 3-letter codes supported by backend that don't have standard 2-letter equivalents
 * These will use the 3-letter code as both the key and value for consistency
 */
export const BACKEND_ONLY_3_LETTER_CODES: { [key: string]: string } = {
  'arc': 'arc', // Aramaic
  'bar': 'bar', // Austro-Bavarian German
  'ber': 'ber', // Berber
  'bjz': 'bjz', // Belizean Creole
  'bwg': 'bwg', // Chibarwe
  'cal': 'cal', // Carolinian
  'ckb': 'ckb', // Sorani
  'cnr': 'cnr', // Montenegrin
  'crs': 'crs', // Seychellois Creole
  'fil': 'fil', // Filipino
  'gil': 'gil', // Gilbertese
  'gsw': 'gsw', // Swiss German
  'hgm': 'hgm', // Khoekhoe
  'hif': 'hif', // Fiji Hindi
  'jam': 'jam', // Jamaican Patois
  'kck': 'kck', // Kalanga
  'khi': 'khi', // Khoisan
  'kwn': 'kwn', // Kwangali
  'loz': 'loz', // Lozi
  'lua': 'lua', // Tshiluba
  'mey': 'mey', // Hassaniya
  'mfe': 'mfe', // Mauritian Creole
  'ndc': 'ndc', // Ndau
  'nfr': 'nfr', // Guernésiais
  'niu': 'niu', // Niuean
  'nrf': 'nrf', // Jèrriais
  'nso': 'nso', // Northern Sotho
  'nzs': 'nzs', // New Zealand Sign Language
  'pap': 'pap', // Papiamento
  'pau': 'pau', // Palauan
  'pih': 'pih', // Norfuk
  'pov': 'pov', // Upper Guinea Creole
  'prs': 'prs', // Dari
  'pus': 'pus', // Pashto
  'rar': 'rar', // Cook Islands Māori
  'smi': 'smi', // Sami
  'tet': 'tet', // Tetum
  'tkl': 'tkl', // Tokelauan
  'toi': 'toi', // Tonga (Zimbabwe)
  'tpi': 'tpi', // Tok Pisin
  'tvl': 'tvl', // Tuvaluan
  'zdj': 'zdj', // Comorian
  'zib': 'zib', // Zimbabwean Sign Language
};

/**
 * Mapping from 3-letter (ISO 639-3) to 2-letter (ISO 639-1) language codes
 * Auto-generated from LANGUAGE_CODE_2_TO_3
 */
export const LANGUAGE_CODE_3_TO_2: { [key: string]: string } = (() => {
  const mapping: { [key: string]: string } = {};

  // Add standard 2-letter to 3-letter mappings (reversed)
  Object.entries(LANGUAGE_CODE_2_TO_3).forEach(([key, value]) => {
    mapping[value] = key;
  });

  // Add backend-only 3-letter codes (map to themselves as they have no 2-letter equivalent)
  Object.keys(BACKEND_ONLY_3_LETTER_CODES).forEach(code => {
    mapping[code] = code;
  });

  return mapping;
})();

/**
 * Convert 2-letter language code to 3-letter code
 * @param code - 2-letter ISO 639-1 language code (e.g., 'en', 'de', 'es')
 * @returns 3-letter ISO 639-3 language code (e.g., 'eng', 'deu', 'spa')
 * @default Returns 'eng' if code is not found
 */
export function convertTwoToThreeLetterCode(code: string): string {
  const lowerCode = code.toLowerCase();
  return LANGUAGE_CODE_2_TO_3[lowerCode] || lowerCode || 'eng';
}

/**
 * Convert 3-letter language code to 2-letter code
 * @param code - 3-letter ISO 639-3 language code (e.g., 'eng', 'deu', 'spa')
 * @returns 2-letter ISO 639-1 language code (e.g., 'en', 'de', 'es')
 * @default Returns the original 3-letter code if no 2-letter equivalent exists, or 'en' if not found
 */
export function convertThreeToTwoLetterCode(code: string): string {
  const lowerCode = code.toLowerCase();
  return LANGUAGE_CODE_3_TO_2[lowerCode] || lowerCode || 'en';
}

/**
 * Check if a language code is valid (either 2-letter or 3-letter)
 * @param code - Language code to validate
 * @returns true if the code is valid, false otherwise
 */
export function isValidLanguageCode(code: string): boolean {
  const lowerCode = code.toLowerCase();
  return (
    LANGUAGE_CODE_2_TO_3.hasOwnProperty(lowerCode) ||
    LANGUAGE_CODE_3_TO_2.hasOwnProperty(lowerCode) ||
    BACKEND_ONLY_3_LETTER_CODES.hasOwnProperty(lowerCode)
  );
}
